import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Fullscreen,
  FullscreenExit,
  ContentCopy,
  OpenInNew,
  Download,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { resolveQuestionHtmlAssetUrl } from "../../../api";

const extractMarkdownImageRefs = (markdown) => {
  if (!markdown || typeof markdown !== "string") return [];
  const seen = new Set();
  const out = [];
  const push = (rawUrl, alt) => {
    const u = (rawUrl || "").trim();
    if (!u || u.startsWith("data:")) return;
    const key = u.split("?")[0];
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ url: u, alt: (alt || "").slice(0, 200) });
  };
  let m;
  const reMd = /!\[([^\]]*)\]\(([^)\s]+)\)/g;
  while ((m = reMd.exec(markdown)) !== null) {
    push(m[2], m[1]);
  }
  const reImg = /<img[^>]+src=["']([^"']+)["'][^>]*>/gi;
  while ((m = reImg.exec(markdown)) !== null) {
    const altMatch = /alt=["']([^"']*)["']/i.exec(m[0]);
    push(m[1], altMatch ? altMatch[1] : "");
  }
  return out;
};

const downloadTextAsFile = (filename, text) => {
  const safe = (filename || "datalab-markdown.md").replace(/[^\w.\- ()[\]]+/g, "_").trim() || "datalab-markdown.md";
  const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
  const a = document.createElement("a");
  const u = URL.createObjectURL(blob);
  a.href = u;
  a.download = safe.endsWith(".md") ? safe : `${safe}.md`;
  a.click();
  URL.revokeObjectURL(u);
};

export default function ImportMarkdownViewerDialog({ open, onClose, importId, filename }) {
  const [markdownContent, setMarkdownContent] = useState("");
  const [markdownLoading, setMarkdownLoading] = useState(false);
  const [markdownTab, setMarkdownTab] = useState(0);
  const [markdownJsonContent, setMarkdownJsonContent] = useState(null);
  const [markdownJsonLoading, setMarkdownJsonLoading] = useState(false);
  const [markdownFullscreen, setMarkdownFullscreen] = useState(false);
  const markdownPreviewRef = useRef(null);

  const markdownImageList = useMemo(
    () => extractMarkdownImageRefs(markdownContent),
    [markdownContent]
  );

  const displayName = filename || (importId ? `Import #${importId}` : "");

  useEffect(() => {
    setMarkdownJsonContent(null);
  }, [importId]);

  useEffect(() => {
    if (!open || !importId) return;
    let cancelled = false;
    setMarkdownLoading(true);
    setMarkdownTab(0);
    setMarkdownJsonContent(null);
    setMarkdownContent("");
    api
      .get(`/admin/question-bank/import/${importId}/markdown`)
      .then((res) => {
        if (!cancelled) {
          setMarkdownContent(res.data?.data?.markdown || "No markdown content available.");
        }
      })
      .catch(() => {
        if (!cancelled) setMarkdownContent("Failed to load markdown content.");
      })
      .finally(() => {
        if (!cancelled) setMarkdownLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, importId]);

  useEffect(() => {
    if (!open || markdownTab !== 0 || markdownLoading) return;
    const el = markdownPreviewRef.current;
    if (!el) return;
    let iv = null;
    const typeset = () => {
      if (window.MathJax?.typesetPromise) {
        window.MathJax.typesetPromise([el]).catch(() => {});
        return true;
      }
      return false;
    };
    const kick = () => {
      if (typeset()) return;
      if (window.MathJax?.startup?.promise) {
        window.MathJax.startup.promise
          .then(() => typeset())
          .catch(() => typeset());
        return;
      }
      const onReady = () => typeset();
      document.addEventListener("mathjax:ready", onReady, { once: true });
      const deadline = Date.now() + 15000;
      iv = setInterval(() => {
        if (typeset() || Date.now() > deadline) {
          clearInterval(iv);
          iv = null;
        }
      }, 200);
    };
    const t = setTimeout(kick, 0);
    return () => {
      clearTimeout(t);
      if (iv) clearInterval(iv);
    };
  }, [open, markdownTab, markdownLoading, markdownContent]);

  const handleMarkdownTabChange = useCallback(async (_, newTab) => {
    setMarkdownTab(newTab);
    if (newTab === 3 && markdownJsonContent === null && importId) {
      setMarkdownJsonLoading(true);
      try {
        const res = await api.get(`/admin/question-bank/import/${importId}/datalab`);
        setMarkdownJsonContent(res.data?.data ?? null);
      } catch {
        setMarkdownJsonContent({ error: "Failed to load JSON artifacts." });
      } finally {
        setMarkdownJsonLoading(false);
      }
    }
  }, [importId, markdownJsonContent]);

  const copyMarkdownToClipboard = () => {
    const t = markdownContent || "";
    navigator.clipboard.writeText(t).then(
      () => toast.success("Markdown copied"),
      () => toast.error("Copy failed")
    );
  };

  const copyAllImageUrls = () => {
    const body = markdownImageList.map((x) => resolveQuestionHtmlAssetUrl(x.url)).join("\n");
    navigator.clipboard.writeText(body).then(
      () => toast.success(`${markdownImageList.length} URL(s) copied`),
      () => toast.error("Copy failed")
    );
  };

  const tryDownloadImage = async (rawUrl, index) => {
    const resolved = resolveQuestionHtmlAssetUrl(rawUrl);
    const base = (() => {
      try {
        const u = new URL(resolved, window.location.origin);
        const seg = u.pathname.split("/").filter(Boolean).pop() || `image-${index + 1}`;
        return seg.includes(".") ? seg : `${seg}.png`;
      } catch {
        return `image-${index + 1}.png`;
      }
    })();
    try {
      const r = await fetch(resolved, { mode: "cors" });
      if (!r.ok) throw new Error("fetch");
      const blob = await r.blob();
      const a = document.createElement("a");
      const obj = URL.createObjectURL(blob);
      a.href = obj;
      a.download = base.slice(0, 180);
      a.click();
      URL.revokeObjectURL(obj);
      toast.success("Download started");
    } catch {
      window.open(resolved, "_blank", "noopener,noreferrer");
      toast.info("Opened in a new tab — use Save if the file did not download (CORS).");
    }
  };

  const handleClose = () => {
    setMarkdownFullscreen(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      fullScreen={markdownFullscreen}
      PaperProps={{
        sx: markdownFullscreen
          ? { m: 0, maxHeight: "100%" }
          : { maxHeight: "min(92vh, 920px)" },
      }}
    >
      <DialogTitle
        sx={{
          pb: 0,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 200px" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            DataLab markdown
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {displayName}
            {importId ? ` · job #${importId}` : ""}
          </Typography>
          <Tabs
            value={markdownTab}
            onChange={handleMarkdownTabChange}
            sx={{ mt: 1, borderBottom: 1, borderColor: "divider" }}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Preview" sx={{ textTransform: "none", fontSize: "0.8rem", minHeight: 36 }} />
            <Tab label="Raw" sx={{ textTransform: "none", fontSize: "0.8rem", minHeight: 36 }} />
            <Tab
              label={`Images${markdownImageList.length ? ` (${markdownImageList.length})` : ""}`}
              sx={{ textTransform: "none", fontSize: "0.8rem", minHeight: 36 }}
            />
            <Tab label="JSON" sx={{ textTransform: "none", fontSize: "0.8rem", minHeight: 36 }} />
          </Tabs>
        </Box>
        <Tooltip title={markdownFullscreen ? "Exit full screen" : "Full screen"}>
          <IconButton
            size="small"
            onClick={() => setMarkdownFullscreen((v) => !v)}
            sx={{ mt: 0.5 }}
            aria-label="Toggle full screen"
          >
            {markdownFullscreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: "flex", flexDirection: "column", minHeight: markdownFullscreen ? "70vh" : 360 }}>
        {markdownTab === 0 &&
          (markdownLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              ref={markdownPreviewRef}
              sx={{
                fontSize: "0.95rem",
                lineHeight: 1.65,
                "& img": {
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  display: "block",
                  my: 1,
                },
                "& table": { width: "100%", borderCollapse: "collapse", my: 1, fontSize: "0.88rem" },
                "& th, & td": { border: "1px solid #ccc", p: 0.75 },
                "& h1": { fontSize: "1.5rem", mt: 2, mb: 1 },
                "& h2": { fontSize: "1.25rem", mt: 1.5, mb: 0.75 },
                "& ul, & ol": { pl: 2.5, my: 0.5 },
                "& pre, & code": {
                  fontFamily: "monospace",
                  fontSize: "0.85em",
                  bgcolor: "#f0f0f0",
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.5,
                },
                "& pre": { display: "block", p: 1.5, overflow: "auto" },
                "& mjx-container": { my: 0.5 },
                backgroundColor: "#fafafa",
                p: 2.5,
                flex: 1,
                overflow: "auto",
                maxHeight: markdownFullscreen ? "none" : "min(72vh, 720px)",
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  img: ({ src, alt, ...props }) => {
                    const resolved = src ? resolveQuestionHtmlAssetUrl(src) : "";
                    return (
                      <Box
                        component="img"
                        src={resolved || src}
                        alt={alt || ""}
                        {...props}
                        sx={{
                          maxWidth: "100%",
                          height: "auto",
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                          display: "block",
                          my: 1,
                        }}
                        onError={(e) => {
                          e.target.style.opacity = 0.35;
                        }}
                      />
                    );
                  },
                }}
              >
                {markdownContent || "No content available."}
              </ReactMarkdown>
            </Box>
          ))}

        {markdownTab === 1 &&
          (markdownLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2.5,
                fontFamily: "monospace",
                fontSize: "0.8rem",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                backgroundColor: "#1e1e1e",
                color: "#d4d4d4",
                flex: 1,
                overflow: "auto",
                maxHeight: markdownFullscreen ? "none" : "min(72vh, 720px)",
              }}
            >
              {markdownContent || "No content available."}
            </Box>
          ))}

        {markdownTab === 2 &&
          (markdownLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ p: 2, flex: 1, overflow: "auto", maxHeight: markdownFullscreen ? "none" : "min(72vh, 720px)" }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Images referenced in the DataLab markdown ({markdownImageList.length}). Resolved URLs match the review page (proxy / R2).
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ContentCopy />}
                  disabled={markdownImageList.length === 0}
                  onClick={copyAllImageUrls}
                  sx={{ textTransform: "none" }}
                >
                  Copy all URLs
                </Button>
              </Box>
              {markdownImageList.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No markdown image references found.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                    gap: 1.5,
                  }}
                >
                  {markdownImageList.map((item, idx) => {
                    const resolved = resolveQuestionHtmlAssetUrl(item.url);
                    return (
                      <Paper key={`${resolved}-${idx}`} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                        <Box
                          component="img"
                          src={resolved}
                          alt={item.alt}
                          sx={{
                            width: "100%",
                            height: 140,
                            objectFit: "contain",
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            mb: 0.75,
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, wordBreak: "break-all" }}>
                          {item.alt || "(no alt)"}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<OpenInNew sx={{ fontSize: "1rem !important" }} />}
                            href={resolved}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ textTransform: "none", minWidth: 0 }}
                          >
                            Open
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Download sx={{ fontSize: "1rem !important" }} />}
                            onClick={() => tryDownloadImage(item.url, idx)}
                            sx={{ textTransform: "none", minWidth: 0 }}
                          >
                            Save
                          </Button>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Box>
          ))}

        {markdownTab === 3 &&
          (markdownJsonLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2.5,
                fontFamily: "monospace",
                fontSize: "0.75rem",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                backgroundColor: "#1e1e1e",
                color: "#9cdcfe",
                flex: 1,
                overflow: "auto",
                maxHeight: markdownFullscreen ? "none" : "min(72vh, 720px)",
              }}
            >
              {markdownJsonContent === null
                ? "Open this tab to load DataLab JSON…"
                : JSON.stringify(markdownJsonContent, null, 2)}
            </Box>
          ))}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 2, py: 1.5 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Download />}
          disabled={markdownLoading || !(markdownContent || "").trim()}
          onClick={() =>
            downloadTextAsFile(
              (displayName || "datalab").replace(/\.pdf$/i, "") + "-markdown.md",
              markdownContent || ""
            )
          }
          sx={{ textTransform: "none" }}
        >
          Download .md
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentCopy />}
          disabled={markdownLoading || !(markdownContent || "").trim()}
          onClick={copyMarkdownToClipboard}
          sx={{ textTransform: "none" }}
        >
          Copy markdown
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
